import { createDataLayerSurface, readDataLayerFeatures } from './data-layer-rendering.js';
import { DataLayerViewLoader } from './data-layer-view-loader.js';
import { disposeRemovedLayer } from './layer-disposal.js';
import { showMapRouteCard } from './map-route-card.js';
import { dataMarkerDetails } from './data-marker-details.js';
import { canRenderPoints, pointAtPixel } from './data-point-renderer.js';
import WebGLVectorLayer from 'ol/layer/WebGLVector.js';
import { datasetExtent } from './local-dataset-extent.js';
import './local-dataset.css';
import { runImportWorker } from './import-worker-client.js';
import { layerForFeature } from './local-dataset.js';
import { transformExtent } from 'ol/proj.js';
import { ScreenImageCanvas } from './data-image-render.js';
import { createElement, Layers, Pencil } from 'lucide';
import { closeDataDialogOnBackdrop } from './data-layer-dialog.js';
import { dataFeatureSourceName, visibleDataFeatures } from '../shared/data-layer-display.js';
import { dataLayersGeoJson } from './data-layer-geojson.js';
import { dataFieldLabel, renderDataField } from './data-feature-fields.js';
import { openDataFeatureDetails } from './data-feature-details.js';
import { trackDataSummary } from './track-data-summary.js';
import { boundingExtent, getCenter, buffer, containsExtent } from 'ol/extent.js';
import { GlobeDataLayers } from './globe-data-layers.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorImageLayer from 'ol/layer/VectorImage.js';
import VectorSource from 'ol/source/Vector.js';
import type Feature from 'ol/Feature.js';
import type OlMap from 'ol/Map.js';
import type { DataFeature, DataLayer, DataLayerProvenance } from '../shared/data-layer.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isObject, isString } from '../shared/runtime-values.js';
import { dataFeatureName } from './data-feature-name.js';
import { t } from './i18n.js';
import { editDataFeature } from './data-feature-edit.js';

export interface DataFeatureSelection { layerId: string; featureId: string }
interface SelectionOptions { keepDrawings: boolean; layerId: string | null }

interface RuntimeEntry {
  source: VectorSource;
  layer: VectorLayer<VectorSource> | VectorImageLayer<VectorSource> | WebGLVectorLayer<VectorSource>;
  loadedExtent?: number[];
  data: DataLayer;
  displayed: DataFeature[];
  markerDetails?: ReturnType<typeof dataMarkerDetails>;
}

export function exportDataLayerGeoJson(layer: DataLayer): string {
  return JSON.stringify(dataLayersGeoJson([layer]));
}

export class DataLayerRuntime {
  private readonly entries = new Map<string, RuntimeEntry>();
  private readonly globe = new GlobeDataLayers();
  private clickListener: ((event: { pixel: number[]; originalEvent?: { pointerType?: string } }) => void) | null = null;
  private selectionListener: ((selection: DataFeatureSelection[], options: SelectionOptions) => void) | null = null;
  private selectedFeatures = new Map<string, Set<string>>();
  private cancelEdit: (() => void) | null = null;

  private readonly screens: ScreenImageCanvas;
  private readonly views = new DataLayerViewLoader({
    read: (id: string) => {
      const entry = this.entries.get(id);
      if (!entry?.data.dataset || !entry.data.visible || this.cancelEdit) return undefined;
      const viewExtent = this.map.getView().calculateExtent(this.map.getSize());
      if (entry.loadedExtent && containsExtent(entry.loadedExtent, viewExtent)) return undefined;
      const loadedExtent = buffer(viewExtent, Math.max(viewExtent[2] - viewExtent[0], viewExtent[3] - viewExtent[1]) / 4);
      return { layer: entry.data, ref: entry.data.dataset, loadedExtent, extent: transformExtent(loadedExtent, this.map.getView().getProjection(), 'EPSG:4326') };
    },
    load: ({ layer, ref, extent }) => runImportWorker('datasetView', { ref, extent: datasetExtent(extent), display: layer.display }),
    current: (id, source) => this.entries.get(id)?.data === source.layer && !this.cancelEdit,
    apply: (id, { layer, loadedExtent }, result) => {
      const selected = this.selection.filter(item => item.layerId === id).flatMap(item => { const feature = this.feature(id, item.featureId); return feature ? [feature] : []; });
      const ids = new Set(selected.map(feature => feature.id));
      this.add(layer, [...selected, ...result.features.filter(feature => !ids.has(feature.id))]);
      this.entries.get(id)!.loadedExtent = loadedExtent;
      this.viewStatus.textContent = '';
    },
    failed: (id, error) => {
      if (this.entries.has(id)) this.viewStatus.textContent = t('data_layers.local_read_failed');
      console.warn('Local dataset view failed:', error);
    },
  });
  private readonly gpuFailed = new Set<string>();
  private readonly viewStatus = document.createElement('div');
  private readonly refreshViews = () => {
    if (this.cancelEdit) return;
    for (const entry of this.entries.values()) if (entry.data.dataset && entry.data.visible) void this.loadView(entry.data);
  };

  feature(layerId: string, featureId: string): DataFeature | undefined {
    const entry = this.entries.get(layerId);
    return entry?.displayed.find(feature => feature.id === featureId) ?? entry?.data.features.find(feature => feature.id === featureId);
  }

  private loadView(layer: DataLayer): Promise<void> { return this.views.request(layer.id); }

  constructor(private readonly map: OlMap) {
    const viewport = map.getViewport();
    if (!viewport) throw new Error('Map viewport is not available.');
    this.screens = new ScreenImageCanvas(viewport, () => [...this.entries.values()].map(entry => entry.data));
    map.on('postrender', this.screens.draw);
    map.on('moveend', this.refreshViews);
    this.viewStatus.className = 'local-dataset-status';
    this.viewStatus.setAttribute('role', 'status');
    viewport.appendChild(this.viewStatus);
  }

  add(layer: DataLayer, loaded?: DataFeature[]): void {
    if (layer.provenance) this.map.getView().setMinZoom(0);
    const selected = this.selection;
    const previous = this.entries.get(layer.id);
    const displayed = visibleDataFeatures(loaded ? { ...layer, features: loaded } : layer).filter(feature => !feature.image || feature.image.visible);
    const gpu = !this.gpuFailed.has(layer.id) && canRenderPoints(layer, displayed);
    if (loaded && previous?.data === layer && previous.layer instanceof WebGLVectorLayer && gpu) {
      const ids = new Set(displayed.map(feature => feature.id));
      const removed = previous.source.getFeatures().filter(feature => !ids.has(String(feature.getId())));
      const added = displayed.filter(feature => !previous.source.getFeatureById(feature.id));
      if (removed.length) previous.source.removeFeatures(removed);
      if (added.length) previous.source.addFeatures(this.readFeatures(layer, added));
      previous.displayed = displayed;
      previous.markerDetails = undefined;
      this.globe.replace({ ...layer, features: displayed });
      return;
    }
    this.remove(layer.id, loaded !== undefined);
    const { source, vectorLayer } = createDataLayerSurface(this.map, layer, displayed, gpu, () => this.selectedFeatures.get(layer.id));
    if (vectorLayer instanceof WebGLVectorLayer) {
      vectorLayer.on('postrender', event => {
        const canvas = event.context?.canvas;
        if (!(canvas instanceof HTMLCanvasElement) || canvas.dataset.pointLossHandler) return;
        canvas.dataset.pointLossHandler = 'true';
        canvas.addEventListener('webglcontextlost', () => {
          requestAnimationFrame(() => {
            for (const [id, entry] of this.entries) {
              if (!(entry.layer instanceof WebGLVectorLayer)) continue;
              this.gpuFailed.add(id);
              this.add(entry.data, entry.displayed);
            }
          });
        }, { once: true });
      });
    }
    vectorLayer.setZIndex(5 + layer.position);
    vectorLayer.set('__dataLayerId', layer.id);
    vectorLayer.set('__dataRenderer', gpu ? 'webgl' : 'canvas');
    this.map.addLayer(vectorLayer);
    this.entries.set(layer.id, { source, layer: vectorLayer, data: layer, displayed });
    this.screens.refresh();
    this.globe.replace(loaded ? { ...layer, features: loaded } : layer);
    if (layer.dataset && loaded === undefined) void this.loadView(layer);
    if (selected.length) this.setSelection(selected.filter((item) => item.layerId !== layer.id || (layer.visible && source.getFeatureById(item.featureId))), true);
  }

  private readFeatures(layer: DataLayer, displayed: DataFeature[]): Feature[] {
    return readDataLayerFeatures(this.map, layer, displayed, this.selectedFeatures.get(layer.id));
  }

  markerDetails() {
    return [...this.entries.values()].filter(entry => entry.layer.getVisible()).flatMap(entry => {
      entry.markerDetails ??= entry.displayed.flatMap(feature => dataMarkerDetails(feature, entry.data));
      return entry.markerDetails;
    });
  }

  has(layerId: string): boolean { return this.entries.has(layerId); }

  replace(layer: DataLayer): void { this.add(layer); }

  remove(layerId: string, refreshing = false): void {
    if (!refreshing) this.views.invalidate(layerId);
    this.globe.remove(layerId);
    this.cancelEdit?.();
    const entry = this.entries.get(layerId);
    if (!entry) return;
    this.map.removeLayer(entry.layer);
    disposeRemovedLayer(this.map, entry.layer);
    this.entries.delete(layerId);
    if (!this.entries.size) this.viewStatus.textContent = '';
    this.screens.refresh();
    if (this.selectedFeatures.has(layerId)) this.setSelection(this.selection.filter((item) => item.layerId !== layerId), true);
  }

  setVisible(layerId: string, visible: boolean): void {
    const entry = this.entries.get(layerId);
    if (entry) entry.data.visible = visible;
    this.screens.refresh();
    this.entries.get(layerId)?.layer.setVisible(visible); this.globe.setVisible(layerId, visible);
    if (!visible) this.setSelection(this.selection.filter((item) => item.layerId !== layerId), true);
    else if (entry?.data.dataset) void this.loadView(entry.data);
  }

  get selection(): DataFeatureSelection[] {
    return [...this.selectedFeatures].flatMap(([layerId, ids]) => [...ids].map((featureId) => ({ layerId, featureId })));
  }

  private setSelection(selection: DataFeatureSelection[], keepDrawings: boolean, layerId: string | null = null): void {
    const previous = this.selectedFeatures;
    this.selectedFeatures = new Map();
    for (const item of selection) {
      if (this.entries.get(item.layerId)?.data.display?.locked) continue;
      const ids = this.selectedFeatures.get(item.layerId) ?? new Set<string>();
      ids.add(item.featureId);
      this.selectedFeatures.set(item.layerId, ids);
    }
    this.entries.forEach((entry, id) => {
      if (entry.layer instanceof WebGLVectorLayer) {
        const next = this.selectedFeatures.get(id) ?? new Set<string>();
        const before = previous.get(id) ?? new Set<string>();
        for (const featureId of new Set([...before, ...next])) {
          if (before.has(featureId) !== next.has(featureId)) entry.source.getFeatureById(featureId)?.set('pointSelected', next.has(featureId) ? 1 : 0);
        }
      }
      entry.layer.changed();
    });
    this.selectionListener?.(this.selection, { keepDrawings, layerId });
  }

  selectAll(): void {
    const selection = [...this.entries].flatMap(([layerId, entry]) => entry.layer.getVisible()
      ? entry.source.getFeatures().map((feature) => ({ layerId, featureId: String(feature.getId()) })) : []);
    this.setSelection(selection, true);
  }

  selectInBox(start: { x: number; y: number }, end: { x: number; y: number }, additive: boolean): void {
    const extent = boundingExtent([[start.x, start.y], [end.x, end.y]]);
    const selection = additive ? this.selection : [];
    for (const [layerId, entry] of this.entries) {
      if (!entry.layer.getVisible()) continue;
      for (const feature of entry.source.getFeatures()) {
        const geometry = feature.getGeometry()?.clone();
        if (!geometry) continue;
        geometry.applyTransform((input, output = [], dimension = 2, stride = dimension) => {
          for (let i = 0; i < input.length; i += stride) {
            const pixel = this.map.getPixelFromCoordinate([input[i], input[i + 1]]);
            output[i] = pixel[0]; output[i + 1] = pixel[1];
            for (let j = 2; j < stride; j++) output[i + j] = input[i + j];
          }
          return output;
        });
        if (geometry.intersectsExtent(extent)) selection.push({ layerId, featureId: String(feature.getId()) });
      }
    }
    this.setSelection(selection, true);
  }

  fit(layerId: string): void {
    this.globe.fit(layerId);
    const entry = this.entries.get(layerId);
    const extent = entry?.data.dataset && entry.data.bounds ? transformExtent(entry.data.bounds, 'EPSG:4326', this.map.getView().getProjection()) : entry?.source.getExtent();
    if (!extent || extent.some((value: number) => !Number.isFinite(value))) return;
    this.map.getView().fit(extent, { padding: [60, 60, 60, 60], duration: 300, maxZoom: 16 });
  }

  onFeatureSelected(listener: (selection: DataFeatureSelection[], options: SelectionOptions) => void, canSelect: () => boolean): void {
    this.selectionListener = listener;
    if (this.clickListener) /* SAFETY: The same listener is registered below for this OpenLayers event name. */ this.map.un('singleclick', this.clickListener as never);
    this.clickListener = (event) => {
      if (this.cancelEdit) return;
      if (canSelect() && !this.selectAtPixel(event.pixel, event.originalEvent?.pointerType)) this.selectFeature(null, null);
    };
    // SAFETY: The listener reads the pixel field supplied by an OpenLayers singleclick event.
    this.map.on('singleclick', this.clickListener as never);
  }

  private isOnlySelectedFeature(selection: DataFeatureSelection): boolean {
    return this.selection.length === 1 && this.selectedFeatures.get(selection.layerId)?.has(selection.featureId) === true;
  }

  selectAtPixel(pixel: number[], pointerType?: string, additive = false): boolean {
    if (this.cancelEdit) return false;
    let selection = this.map.forEachFeatureAtPixel(pixel, (feature, layer) => {
      const layerId = layer?.get('__dataLayerId');
      const featureId = feature.get('__dataFeatureId');
      return isString(layerId) && isString(featureId) ? { layerId, featureId } : undefined;
    }, { hitTolerance: pointerType === 'touch' ? 22 : 8, layerFilter: layer => !(layer instanceof WebGLVectorLayer) });
    {
      const entries = [...this.entries.values()].filter(entry => entry.layer instanceof WebGLVectorLayer && entry.layer.getVisible()).sort((a, b) => b.data.position - a.data.position);
      for (const entry of entries) {
        if (selection && entry.data.position < (this.entries.get(selection.layerId)?.data.position ?? -1)) break;
        const feature = pointAtPixel(entry.source, this.map, pixel, pointerType === 'touch' ? 22 : 8);
        if (feature) { selection = { layerId: entry.data.id, featureId: String(feature.getId()) }; break; }
      }
    }
    if (additive) {
      if (selection) {
        const selected = this.selectedFeatures.get(selection.layerId)?.has(selection.featureId);
        this.setSelection(selected ? this.selection.filter((item) => item.layerId !== selection.layerId || item.featureId !== selection.featureId)
          : [...this.selection, selection], true);
      }
    } else if (selection && !this.isOnlySelectedFeature(selection)) this.selectFeature(selection.layerId, selection.featureId);
    return !!selection;
  }

  selectFeature(layerId: string | null, featureId: string | null, fit = false): void {
    const entry = layerId ? this.entries.get(layerId) : undefined;
    if (entry?.data.dataset && featureId && !entry.source.getFeatureById(featureId)) {
      const expected = entry.data;
      void layerForFeature(expected, featureId).then(loaded => {
        if (this.entries.get(expected.id)?.data !== expected) return;
        this.add(expected, [...loaded.features, ...entry.displayed]);
        if (this.entries.get(expected.id)?.source.getFeatureById(featureId)) this.selectFeature(expected.id, featureId, fit);
        else this.setSelection([], false, expected.id);
      }).catch(() => { this.viewStatus.textContent = t('data_layers.local_read_failed'); });
      return;
    }
    this.cancelEdit?.();
    this.setSelection(layerId && featureId ? [{ layerId, featureId }] : [], false, layerId);
    const geometry = layerId && featureId ? this.entries.get(layerId)?.source.getFeatureById(featureId)?.getGeometry() : null;
    if (fit && geometry) this.map.getView().animate({ center: getCenter(geometry.getExtent()), duration: 300 });
  }

  selectFeatures(selection: DataFeatureSelection[]): void {
    this.cancelEdit?.();
    this.setSelection(selection, false);
  }

  async edit(layer: DataLayer, featureId: string, onDone: (changed: DataLayer | null) => void): Promise<void> {
    try { layer = await layerForFeature(layer, featureId); }
    catch { this.viewStatus.textContent = t('data_layers.local_read_failed'); return; }
    if (layer.features.some(feature => feature.id === featureId && feature.image)) return;
    this.cancelEdit?.();
    const feature = this.entries.get(layer.id)?.source.getFeatureById(featureId);
    if (!feature || Array.isArray(feature)) return;
    this.cancelEdit = editDataFeature(this.map, layer, feature, (changed) => {
      this.cancelEdit = null;
      onDone(changed);
    });
  }

  cancelEditing(): void { this.cancelEdit?.(); }

  dispose(): void {
    this.views.dispose();
    this.map.un('postrender', this.screens.draw);
    this.map.un('moveend', this.refreshViews);
    this.viewStatus.remove();
    this.screens.destroy();
    this.globe.destroy();
    this.clear();
    if (this.clickListener) /* SAFETY: The same listener was registered for this OpenLayers event name. */ this.map.un('singleclick', this.clickListener as never);
  }

  clear(): void { [...this.entries.keys()].forEach((id) => this.remove(id)); }
}

function cellText(value: SchemaInput, key: string): string {
  if (key === 'coordinateProperties') {
    const summary = trackDataSummary(value);
    if (summary) return summary;
  }
  if (value === null) return 'null';
  if (isObject(value)) return JSON.stringify(value);
  return String(value ?? '');
}

function message(key: string, replacements: Record<string, string>): string {
  return Object.entries(replacements).reduce((value, [name, replacement]) => value.replace(`{${name}}`, replacement), t(key));
}

function layerProvenanceDetails(layer: DataLayer): HTMLElement {
  const details = document.createElement('details');
  const sources = new Map<string, DataLayerProvenance>();
  for (const provenance of [layer.provenance, ...layer.features.map((feature) => feature.provenance)]) {
    if (provenance) sources.set(JSON.stringify(provenance), provenance);
  }
  if (!sources.size) return document.createElement('div');
  details.className = 'data-layer-provenance mapui:[color:var(--text-secondary)] mapui:text-[12px] display:leading-[1.5] display:max-h-[180px] mapui:[overflow-y:auto] display:p-[0_20px_12px] display:[&_p]:m-[4px_0] display:[&_a]:[color:var(--text-primary)] display:[&_a]:[text-decoration:underline] display:[&_a]:[text-underline-offset:2px] display:[&_summary]:p-[10px_0] display:[&_summary]:[cursor:pointer] display:[&_summary]:font-[600]';
  const summary = document.createElement('summary');
  summary.textContent = [...sources.values()].map((source) => `${source.sourceName} · ${source.license}`).join('; ');
  details.appendChild(summary);
  for (const provenance of sources.values()) {
    const source = document.createElement('p');
    const sourceLink = document.createElement('a');
    sourceLink.href = provenance.sourceUrl;
    sourceLink.textContent = provenance.sourceName;
    sourceLink.target = '_blank';
    sourceLink.rel = 'noopener noreferrer';
    const licenseLink = document.createElement('a');
    licenseLink.href = provenance.licenseUrl;
    licenseLink.textContent = provenance.license;
    licenseLink.target = '_blank';
    licenseLink.rel = 'noopener noreferrer';
    source.appendChild(sourceLink);
    source.append(' · ');
    source.appendChild(licenseLink);
    if (provenance.period) source.append(` · ${provenance.period}`);
    details.appendChild(source);
    for (const text of [provenance.attribution, ...provenance.notes]) {
      const note = document.createElement('p');
      note.textContent = text;
      details.appendChild(note);
    }
  }
  return details;
}

const DATA_TABLE_PAGE_SIZE = 100;
let tableRequest = 0;

export async function openDataLayerTable(layer: DataLayer, page = 0, edit?: (featureId: string) => void): Promise<void> {
  const request = ++tableRequest;
  document.querySelector('#data-layer-dialog')?.remove();
  const columns = layer.dataset?.columns ?? [...new Set(layer.features.flatMap((feature) => Object.keys(feature.properties)))];
  const pageSize = DATA_TABLE_PAGE_SIZE;
  const pages = Math.max(1, Math.ceil(layer.featureCount / pageSize));
  const safePage = Math.max(0, Math.min(pages - 1, page));
  let rows: DataFeature[];
  try { rows = layer.dataset ? await runImportWorker('datasetPage', { ref: layer.dataset, offset: safePage * pageSize, limit: pageSize }) : layer.features.slice(safePage * pageSize, (safePage + 1) * pageSize); }
  catch { if (request === tableRequest) window.alert(t('data_layers.local_read_failed')); return; }
  if (request !== tableRequest) return;
  const dialog = document.createElement('dialog');
  dialog.id = 'data-layer-dialog';
  dialog.className = 'data-layer-dialog layers:m-[auto] layers:w-[min(960px,_calc(100vw_-_32px))] layers:min-w-[min(640px,_calc(100vw_-_32px))] layers:max-h-[min(720px,_calc(100vh_-_32px))] layers:supports-[height:100dvh]:max-h-[min(720px,_calc(100dvh_-_32px))] mapui:p-[0] layers:[border:0] layers:rounded-[18px] mapui:[background:var(--bg-active)] mapui:[color:var(--text-primary)] layers:[box-shadow:0_24px_70px_rgb(15_23_42_/_28%)] layers:[&::backdrop]:[background:rgb(15_23_42_/_35%)] layers:[&[open]]:flex layers:[&[open]]:flex-col layers:[&_header]:flex layers:[&_header]:items-center layers:[&_header]:justify-between layers:[&_header]:p-[14px_18px] layers:[&_footer]:flex layers:[&_footer]:items-center layers:[&_footer]:justify-between layers:[&_footer]:p-[14px_18px] layers:w-max-768:w-[calc(100vw_-_32px)] layers:w-max-768:max-h-[calc(100vh_-_32px)] layers:supports-[height:100dvh]:w-max-768:max-h-[calc(100dvh_-_32px)] layers:w-max-768:min-w-[0]';
  dialog.setAttribute('aria-labelledby', 'data-layer-dialog-title');
  const header = document.createElement('header'); header.className = 'data-layer-dialog-header mapui:gap-[16px] layers:[border-bottom:1px_solid_rgb(15_23_42_/_9%)] layers:w-max-768:pt-[max(14px,_env(safe-area-inset-top))]';
  const heading = document.createElement('div'); heading.className = 'data-layer-dialog-heading mapui:min-w-[0] layers:[&_h2]:m-[0] layers:[&_h2]:[overflow:hidden] layers:[&_h2]:text-[20px] layers:[&_h2]:leading-[1.25] layers:[&_h2]:[text-overflow:ellipsis] layers:[&_h2]:whitespace-nowrap';
  const title = document.createElement('h2');
  title.id = 'data-layer-dialog-title';
  title.textContent = layer.name;
  const meta = document.createElement('p'); meta.className = 'data-layer-dialog-meta layers:m-[4px_0_0] mapui:[color:var(--text-secondary)] mapui:text-[13px]';
  meta.textContent = message('data_layers.table_summary', { count: layer.featureCount.toLocaleString(), columns: columns.length.toLocaleString() });
  const close = document.createElement('button');
  close.type = 'button'; close.className = 'data-layer-dialog-close layers:[border:0] layers:rounded-[10px] layers:[background:var(--bg-hover)] mapui:[color:var(--text-secondary)] mapui:[cursor:pointer] layers:font-inherit mapui:[flex:0_0_auto] mapui:w-[38px] mapui:h-[38px] layers:text-[24px] layers:leading-[1] layers:[&:focus-visible]:[outline:3px_solid_rgb(59_130_246_/_28%)] layers:[&:focus-visible]:[outline-offset:2px]'; close.textContent = '×';
  close.setAttribute('aria-label', message('data_layers.close_table', { name: layer.name }));
  close.addEventListener('click', () => dialog.close());
  heading.appendChild(title); heading.appendChild(meta); header.appendChild(heading); header.appendChild(close);
  const wrap = document.createElement('div'); wrap.className = 'data-layer-table-wrap mapui:min-h-[0] layers:max-h-[56vh] layers:supports-[height:100dvh]:max-h-[56dvh] layers:[overflow:auto] layers:[overscroll-behavior:contain] layers:w-max-768:[flex:1_1_auto] layers:w-max-768:max-h-[none]';
  const table = document.createElement('table'); table.className = 'data-layer-table feature:[&_td.data-track-summary]:[white-space:pre-line] feature:[&_td.data-track-summary]:[overflow-wrap:anywhere] mapui:w-[100%] layers:[border-collapse:collapse] layers:[&_th]:max-w-[280px] layers:[&_th]:p-[8px_12px] layers:[&_th]:[overflow:hidden] layers:[&_th]:[border-bottom:1px_solid_rgb(15_23_42_/_8%)] layers:[&_th]:text-start layers:[&_th]:[text-overflow:ellipsis] layers:[&_th]:whitespace-nowrap layers:[&_th]:sticky layers:[&_th]:z-[var(--layer-local-raised)] layers:[&_th]:top-[0] layers:[&_th]:[background:#f8fafc] layers:[&_th]:[color:var(--text-secondary)] layers:[&_th]:text-[12px] layers:[&_th]:font-[750] layers:[&_th]:tracking-[0.02em] layers:[&_td]:max-w-[280px] layers:[&_td]:p-[8px_12px] layers:[&_td]:[overflow:hidden] layers:[&_td]:[border-bottom:1px_solid_rgb(15_23_42_/_8%)] layers:[&_td]:text-start layers:[&_td]:[text-overflow:ellipsis] layers:[&_td]:whitespace-nowrap display:[&_a]:[color:#2563eb] display:[&_a]:[text-decoration:underline] display:[&_a]:[text-underline-offset:2px]';
  const thead = table.createTHead(); thead.className = 'data-layer-table-head'; const headRow = thead.insertRow();
  columns.forEach((column) => { const th = document.createElement('th'); th.scope = 'col'; th.textContent = column === 'coordinateProperties' ? t('data_layers.track_data') : column; headRow.appendChild(th); });
  if (edit) { const th = document.createElement('th'); th.scope = 'col'; th.setAttribute('aria-label', t('data_layers.edit_fields', 'Edit fields and style')); headRow.appendChild(th); }
  const tbody = table.createTBody();
  rows.forEach((feature) => {
    const row = tbody.insertRow(); row.className = 'data-layer-table-row layers:[&:nth-child(even)]:[background:rgb(248_250_252_/_72%)] layers:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:#eff6ff]';
    columns.forEach((column) => {
      const cell = row.insertCell();
      const value = cellText(feature.properties[column], column);
      renderDataField(cell, column, value); cell.title = value;
    });
    if (edit && !feature.image) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'data-layer-edit-button mapui:inline-flex mapui:items-center mapui:justify-center mapui:w-[36px] mapui:h-[36px] display:[border:1px_solid_var(--border-color)] display:rounded-[8px] mapui:[background:var(--bg-active)] mapui:[color:var(--text-secondary)] mapui:[cursor:pointer] display:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] display:[&:focus-visible]:[outline:2px_solid_#2563eb] display:[&:focus-visible]:[outline-offset:2px]';
      button.title = t('data_layers.edit_fields', 'Edit fields and style');
      button.setAttribute('aria-label', button.title);
      button.appendChild(createElement(Pencil, { width: 18, height: 18, 'aria-hidden': 'true' }));
      button.addEventListener('click', () => { dialog.close(); edit(feature.id); });
      row.insertCell().appendChild(button);
    }
  });
  wrap.appendChild(table);
  const footer = document.createElement('footer'); footer.className = 'data-layer-dialog-footer mapui:gap-[16px] layers:[border-top:1px_solid_rgb(15_23_42_/_9%)] mapui:[background:var(--bg-toolbar)] layers:w-max-768:pb-[max(14px,_env(safe-area-inset-bottom))]';
  const status = document.createElement('span'); status.className = 'data-layer-page-status mapui:[color:var(--text-secondary)] layers:text-[13px] layers:font-[650]'; status.textContent = message('data_layers.page_status', { page: String(safePage + 1), pages: String(pages) });
  const controls = document.createElement('span'); controls.className = 'data-layer-pagination mapui:inline-flex mapui:items-center mapui:gap-[8px]';
  const previous = document.createElement('button'); previous.type = 'button'; previous.className = 'data-layer-pagination-button data-layer-pagination-previous layers:min-h-[36px] layers:p-[7px_12px] layers:[border:1px_solid_rgb(15_23_42_/_14%)] layers:rounded-[9px] mapui:[background:#fff] mapui:[color:var(--text-primary)] mapui:[cursor:pointer] layers:font-inherit layers:text-[13px] layers:font-[700] layers:[&:disabled]:[cursor:default] layers:[&:disabled]:[opacity:0.42] layers:[&:focus-visible]:[outline:3px_solid_rgb(59_130_246_/_28%)] layers:[&:focus-visible]:[outline-offset:2px]'; previous.textContent = t('data_layers.previous'); previous.disabled = safePage === 0;
  const next = document.createElement('button'); next.type = 'button'; next.className = 'data-layer-pagination-button data-layer-pagination-next layers:min-h-[36px] layers:p-[7px_12px] layers:[border:1px_solid_rgb(15_23_42_/_14%)] layers:rounded-[9px] mapui:[background:#fff] mapui:[color:var(--text-primary)] mapui:[cursor:pointer] layers:font-inherit layers:text-[13px] layers:font-[700] layers:[&:disabled]:[cursor:default] layers:[&:disabled]:[opacity:0.42] layers:[&:focus-visible]:[outline:3px_solid_rgb(59_130_246_/_28%)] layers:[&:focus-visible]:[outline-offset:2px]'; next.textContent = t('data_layers.next'); next.disabled = safePage === pages - 1;
  previous.addEventListener('click', () => { dialog.close(); openDataLayerTable(layer, safePage - 1, edit); });
  next.addEventListener('click', () => { dialog.close(); openDataLayerTable(layer, safePage + 1, edit); });
  controls.appendChild(previous); controls.appendChild(next); footer.appendChild(status); footer.appendChild(controls);
  dialog.appendChild(header); dialog.appendChild(layerProvenanceDetails(layer)); dialog.appendChild(wrap); if (pages > 1) dialog.appendChild(footer); closeDataDialogOnBackdrop(dialog); document.body.appendChild(dialog); dialog.showModal();
  dialog.addEventListener('close', () => dialog.remove(), { once: true });
}

export function showDataFeatureCard(layer: DataLayer | null, feature: DataFeature | null, edit?: () => void, changeColor?: (key: string, color: string) => void, editFields?: (featureId: string) => void, onDismiss?: () => void): void {
  document.querySelector('#data-feature-card')?.remove();
  if (!layer || !feature) return;
  if (showMapRouteCard(layer, { close: () => { document.querySelector('#data-feature-card')?.remove(); onDismiss?.(); } })) return;
  const card = document.createElement('aside'); card.id = 'data-feature-card'; card.className = 'data-feature-card data-feature-summary feature:[&_.data-feature-card-header]:items-start feature:[&_.data-feature-card-header_strong]:text-[19px] feature:[&_>_.data-feature-card-actions]:pt-[16px] feature:[&_>_.data-feature-card-actions]:[border-top:1px_solid_var(--border-color)] feature:[&_.data-feature-card-primary]:min-h-[44px] feature:[&_.data-feature-card-primary]:font-[500] layers:[&_button:focus-visible]:[outline:3px_solid_rgb(59_130_246_/_28%)] layers:[&_button:focus-visible]:[outline-offset:2px] mapui:fixed mapui:z-[var(--layer-floating-panel)] layers:right-[18px] layers:bottom-[var(--layout-floating-panel-bottom)] layers:w-[min(360px,_calc(100vw_-_36px))] layers:max-h-[60vh] layers:supports-[height:100dvh]:max-h-[60dvh] layers:p-[20px] layers:[overflow:auto] layers:[border:1px_solid_rgb(15_23_42_/_12%)] layers:rounded-[16px] mapui:[background:var(--bg-toolbar)] mapui:[color:var(--text-primary)] layers:[box-shadow:0_12px_36px_rgb(15_23_42_/_18%)] layers:w-max-768:bottom-[var(--layout-mobile-panel-bottom)] layers:[&_.data-feature-card-primary]:[flex:1] layers:[&_.data-feature-card-primary]:min-h-[40px] layercontrols:w-max-768:right-[8px] layercontrols:w-max-768:w-[calc(100vw_-_16px)] layercontrols:w-max-768:max-h-[55vh] layercontrols:supports-[height:100dvh]:w-max-768:max-h-[55dvh] layercontrols:w-max-768:p-[16px]';
  card.setAttribute('aria-label', message('data_layers.location_details', { name: layer.name }));
  const entries = Object.entries(feature.properties).filter(([key, value]) => !(['name', 'title', 'label'].includes(key.toLowerCase()) && value === dataFeatureName(feature, layer))).slice(0, 5);
  const title = document.createElement('strong'); title.textContent = dataFeatureName(feature, layer);
  const list = document.createElement('table'); list.className = 'data-feature-fields mapui:w-[100%] layers:[table-layout:fixed] layers:[border-collapse:collapse] layers:[margin-block:10px_14px] mapui:text-[14px] layers:[&_th]:p-[10px_0] layers:[&_th]:[border:0] layers:[&_th]:text-start layers:[&_th]:[vertical-align:top] layers:[&_th]:[overflow-wrap:anywhere] layers:[&_th]:w-[40%] layers:[&_th]:[padding-inline-end:16px] layers:[&_th]:[color:var(--text-secondary)] layers:[&_th]:[background:transparent] layers:[&_th]:font-[400] layers:[&_td]:p-[10px_0] layers:[&_td]:[border:0] layers:[&_td]:text-start layers:[&_td]:[vertical-align:top] layers:[&_td]:[overflow-wrap:anywhere] layers:[&_a]:[color:var(--color-primary,_#2563eb)] layers:[&_a]:[text-decoration:none]';
  const body = list.createTBody();
  entries.forEach(([key, value]) => {
    const row = body.insertRow();
    const term = document.createElement('th'); term.scope = 'row'; term.textContent = dataFieldLabel(key); term.title = key;
    row.appendChild(term); const cell = row.insertCell(); renderDataField(cell, key, cellText(value, key), changeColor);
    const link = cell.querySelector('a');
    if (link) { link.textContent = new URL(link.href).hostname.replace(/^www\./, '') + ' ↗'; link.title = link.href; }
  });
  const actions = document.createElement('div'); actions.className = 'data-feature-card-actions mapui:flex mapui:flex-wrap mapui:items-center mapui:justify-between mapui:gap-[8px] mapui:mt-[12px]';
  const all = document.createElement('button'); all.type = 'button'; all.className = 'data-feature-card-primary data-feature-card-secondary feature:[&[aria-pressed=false]]:[background:var(--bg-active)] feature:[&[aria-pressed=false]]:[color:var(--text-primary)] layers:min-h-[36px] layers:p-[7px_12px] layers:[border:1px_solid_rgb(15_23_42_/_14%)] layers:rounded-[9px] layers:[background:#2563eb] layers:[color:#fff] mapui:[cursor:pointer] layers:font-inherit layers:text-[13px] layers:font-[700] layers:[&.data-feature-card-primary]:[border-color:#2563eb] layers:[&.data-feature-card-primary.data-feature-card-secondary]:[border-color:var(--border-color,_#dce4ee)] layers:[background:var(--bg-toolbar)] layers:[color:var(--text-primary)] layers:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)]'; all.textContent = t('data_layers.view_all_fields'); all.addEventListener('click', () => {
    const page = Math.floor(layer.features.findIndex((item) => item.id === feature.id) / DATA_TABLE_PAGE_SIZE);
    openDataFeatureDetails(layer, feature, () => openDataLayerTable(layer, page, editFields), editFields);
  });
  const close = document.createElement('button'); close.type = 'button'; close.className = 'data-feature-card-dismiss mapui:grid mapui:[place-items:center] layers:[flex:0_0_40px] layers:w-[40px] layers:h-[40px] mapui:p-[0] layers:[border:1px_solid_transparent] layers:rounded-[10px] mapui:[background:transparent] mapui:[color:var(--text-secondary)] layers:font-inherit layers:text-[26px] layers:leading-[1] mapui:[cursor:pointer] layers:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] layers:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[color:var(--text-primary)]'; close.textContent = '×'; close.setAttribute('aria-label', t('shell.close')); close.addEventListener('click', () => { card.remove(); onDismiss?.(); });
  const header = document.createElement('header'); header.className = 'data-feature-card-header mapui:flex mapui:items-center mapui:justify-between mapui:gap-[12px] mapui:min-h-[40px] layers:[&_strong]:min-w-[0] layers:[&_strong]:[overflow-wrap:anywhere]';
  const heading = document.createElement('div'); heading.appendChild(title);
  const subtitle = document.createElement('p'); subtitle.className = 'data-feature-subtitle mapui:m-[5px_0_0] mapui:[color:var(--text-secondary)] mapui:text-[13px] feature:leading-[1.4] mapui:[overflow-wrap:anywhere]'; const layerLink = document.createElement('a');
  layerLink.href = '#data-layer-dialog';
  layerLink.className = 'data-feature-layer-link mapui:inline-flex mapui:items-center mapui:gap-[5px] mapui:[color:var(--color-primary,_#2563eb)] mapui:[text-decoration:underline] display:[text-underline-offset:2px] display:[&:focus-visible]:[outline:2px_solid_#2563eb] display:[&:focus-visible]:[outline-offset:2px]';
  const layerIcon = createElement(Layers, { width: 14, height: 14, 'aria-hidden': 'true' });
  layerIcon.classList.add('mapui:shrink-0');
  const layerName = document.createElement('span');
  layerName.textContent = layer.name;
  layerLink.appendChild(layerIcon);
  layerLink.appendChild(layerName);
  layerLink.setAttribute('aria-haspopup', 'dialog');
  layerLink.addEventListener('click', (event) => {
    event.preventDefault();
    const page = Math.floor(layer.features.findIndex((item) => item.id === feature.id) / DATA_TABLE_PAGE_SIZE);
    openDataLayerTable(layer, page, editFields);
  });
  subtitle.appendChild(layerLink);
  heading.appendChild(subtitle); header.appendChild(heading); header.appendChild(close);
  actions.appendChild(all); card.appendChild(header);
  if (dataFeatureSourceName(feature, layer) === null) {
    const note = document.createElement('p'); note.className = 'data-feature-unnamed-note display:m-[8px_0] mapui:text-[13px]';
    note.textContent = t('data_layers.unnamed_source'); card.appendChild(note);
  }
  if (feature.image?.kind === 'photo') {
    const image = document.createElement('img'); image.src = feature.image.src; image.alt = dataFeatureName(feature, layer); image.style.cssText = 'display:block;max-width:100%;max-height:240px;object-fit:contain';
    const button = document.createElement('button'); button.type = 'button'; button.style.cssText = 'display:block;border:0;background:none;padding:0;cursor:zoom-in'; button.appendChild(image);
    button.addEventListener('click', () => {
      const dialog = document.createElement('dialog');
      const full = document.createElement('img'); full.src = image.src; full.alt = image.alt; full.style.cssText = 'display:block;max-width:85vw;max-height:80vh;object-fit:contain';
      const close = document.createElement('button'); close.type = 'button'; close.textContent = t('shell.close'); close.addEventListener('click', () => dialog.close());
      dialog.appendChild(full); dialog.appendChild(close); closeDataDialogOnBackdrop(dialog); dialog.addEventListener('close', () => dialog.remove()); document.body.appendChild(dialog); dialog.showModal();
    });
    card.appendChild(button);
  }
  card.appendChild(list); card.appendChild(actions); document.body.appendChild(card);
  if (editFields) { const button = document.createElement('button'); button.type = 'button'; button.className = 'data-feature-card-primary feature:[&[aria-pressed=false]]:[background:var(--bg-active)] feature:[&[aria-pressed=false]]:[color:var(--text-primary)] layers:min-h-[36px] layers:p-[7px_12px] layers:[border:1px_solid_rgb(15_23_42_/_14%)] layers:rounded-[9px] layers:[background:#2563eb] mapui:[color:#fff] mapui:[cursor:pointer] layers:font-inherit layers:text-[13px] layers:font-[700] layers:[&.data-feature-card-primary]:[border-color:#2563eb]'; button.textContent = t('data_layers.edit_item'); button.addEventListener('click', () => editFields(feature.id)); actions.insertBefore(button, actions.firstChild); }
  if (edit && !feature.image) {
    const button = document.createElement('button'); button.type = 'button'; button.className = 'data-feature-move mapui:flex mapui:items-center mapui:min-h-[44px] mapui:p-[0_8px] feature:[border:0] feature:rounded-[6px] mapui:[background:transparent] mapui:[color:var(--text-secondary)] feature:font-inherit feature:text-[13px] mapui:[cursor:pointer] feature:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)]'; button.textContent = t(feature.geometry?.type === 'Point' ? 'data_layers.move_point' : 'data_layers.edit_shape');
    const footer = document.createElement('footer'); footer.className = 'data-feature-tools mapui:flex mapui:items-center mapui:justify-between mapui:gap-[12px] mapui:mt-[12px] feature:pt-[8px] mapui:[border-top:1px_solid_var(--border-color)]'; button.addEventListener('click', edit); footer.appendChild(button); card.appendChild(footer);
  }
}
